import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing } from 'lucide-react';
import { Select, MenuItem, FormControl, CircularProgress } from '@mui/material';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';
import TableSkeleton from '../../components/ul/TableSkeleton';
import ImageWithShimmer from '../../components/ul/ImageWithShimmer';
import { IoIosSend } from "react-icons/io";
import ImageDropzone from '../../components/ul/ImageDropzone';
import { imageSizeExceededMessage, imageType, invalidImageMessage, MAX_FILE_SIZE_IMAGE } from '../../components/imageType';
import img1 from '../../Image/NLogo.svg';
import NotFound from '../../components/ul/NotFound';
import DeleteButton from '../../components/ul/DeleteButton';
import { MdDelete } from "react-icons/md";
import { FiSend, FiTrash2, FiExternalLink, FiImage, FiType, FiMessageSquare, FiLink, FiSmartphone } from "react-icons/fi";

const Notification = () => {
    const [loader, setLoader] = useState(false);   // initial / table fetch → shimmer
    const [sending, setSending] = useState(false);  // compose → send button
    const [working, setWorking] = useState(false);  // delete / resend
    const [data, setData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [fileStatus, setFileStatus] = useState({
        image: null,
    });
    const [file, setFile] = useState(null);
    const [resendPlatform, setResendPlatform] = useState('both');
    const [showResendModal, setShowResendModal] = useState(false);
    const [resendInfo, setResendInfo] = useState(null);
    const [formInfo, setFormInfo] = useState({
        title: '',
        message: '',
        url: '',
        platform: 'both'
    })

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getNotification();
            if (resp?.status) {
                setData(resp.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [])

    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete a notification . To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setWorking(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedUsers;
                }

                const response = await Calling.deleteNotification(body);
                if (response?.status) {
                    toast.success(response?.message || 'notification deleted successfully !');
                    setSelectedUsers([]);
                    setSelectAll(false);
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setWorking(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const formData = new FormData();
            formData.append('title', formInfo?.title || '');
            formData.append('message', formInfo?.message || '');
            formData.append('url', formInfo?.url || '');
            formData.append('platform', formInfo?.platform || 'both');
            if (file) {
                formData.append('image', file);
            }
            const response = await Calling.sendNotification(formData);

            if (response?.status) {
                toast.success(response?.message || 'Notification sent successfully!');
                setFormInfo({ title: '', message: '', url: '', platform: 'both' });
                setFile(null);
                setFileStatus({ image: null });
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            fetchData();
            setSending(false);
        }
    }

    // Validate + set the icon file (used by ImageDropzone: drag/drop, browse, paste)
    const processIconFile = (selectedFile) => {
        if (!selectedFile) {
            setFile(null);
            setFileStatus({ image: null });
            return;
        }
        if (!imageType.includes(selectedFile.type)) {
            toast.error(invalidImageMessage);
            setFile(null);
            setFileStatus({ image: 'error' });
            return;
        }
        if (selectedFile.size > MAX_FILE_SIZE_IMAGE) {
            toast.error(imageSizeExceededMessage);
            setFile(null);
            setFileStatus({ image: 'error' });
            return;
        }
        setFile(selectedFile);
        setFileStatus({ image: 'success' });
    };

    const handleFormChange = (e) => {
        setFormInfo({ ...formInfo, [e.target.name]: e.target.value });
    }

    const handleSend = async (info) => {
        setResendInfo(info);
        setResendPlatform(info?.n_platform || 'both');
        setShowResendModal(true);
    }

    const handleResendConfirm = async () => {
        const content = `You are about to Resend a notification to ${resendPlatform === 'both' ? 'both platforms' : resendPlatform} platform. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            setWorking(true);

            try {
                let body = {
                    'title': resendInfo?.n_title || '',
                    'message': resendInfo?.n_message || '',
                    'url': resendInfo?.n_url || '',
                    'platform': resendPlatform
                }

                if (resendInfo?.n_icon) {
                    body.fileName = resendInfo?.n_icon || ''
                }

                const response = await Calling.resendNotification(body);

                if (response?.status) {
                    toast.success(response?.message || 'Notification sent successfully!');
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error submitting form:', error);
            } finally {
                fetchData();
                setWorking(false);
                setShowResendModal(false);
                setResendInfo(null);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    }

    const getPlatformBadge = (platform) => {
        const styles = {
            android: 'bg-main-th text-main-blue border border-main-blue/30',
            ios: 'bg-blue-50 text-blue-500 border border-blue-500/30',
            both: 'bg-red-50 text-red-500 border border-red-500/30'
        };
        const labels = {
            android: 'Android',
            ios: 'iOS',
            both: 'All Platforms'
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${styles[platform] || styles.both}`}>
                <FiSmartphone size={12} />
                {labels[platform] || 'Both'}
            </span>
        );
    };

    return (
        <>
            <div className="w-full min-h-screen text-main-font">
                    {/* Page Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="mb-6"
                    >
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2.5 rounded-xl bg-main-blue text-main-white shadow-lg shadow-main-blue/30">
                                <BellRing size={24} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-main-font">Push Notifications</h1>
                                <p className="text-sm text-main-subfont">Send and manage push notifications</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Send Notification Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.05 }}
                        className="bg-main-white rounded-2xl shadow-sm border border-main-border mb-6 overflow-hidden"
                    >
                        <div className="p-6 border-b border-main-border bg-main-th">
                            <h2 className="text-lg font-semibold text-main-font flex items-center gap-2">
                                <FiSend size={18} className="text-main-blue" />
                                Compose Notification
                            </h2>
                            <p className="text-sm text-main-subfont mt-1">Fill in the details to send a new notification</p>
                        </div>

                        <form onSubmit={handleSubmit} encType="multipart/form-data" className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Title Field */}
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm font-medium text-main-font" htmlFor="title">
                                        <FiType size={16} className="text-main-subfont" />
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formInfo?.title}
                                        onChange={(e) => handleFormChange(e)}
                                        id="title"
                                        className="w-full px-4 py-3 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all"
                                        placeholder="Enter notification title"
                                        required
                                    />
                                </div>

                                {/* Platform Field */}
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm font-medium text-main-font" htmlFor="platform">
                                        <FiSmartphone size={16} className="text-main-subfont" />
                                        Platform
                                    </label>
                                    <FormControl fullWidth>
                                        <Select
                                            name="platform"
                                            value={formInfo?.platform || 'both'}
                                            onChange={handleFormChange}
                                            sx={{ borderRadius: '12px', backgroundColor: '#F9FAFE', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#CFD1D3' } }}
                                        >
                                            <MenuItem value="both">Both (Android &amp; iOS)</MenuItem>
                                            <MenuItem value="android">Android Only</MenuItem>
                                            <MenuItem value="ios">iOS Only</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* Message Field */}
                                <div className="space-y-2 lg:col-span-2">
                                    <label className="flex items-center gap-2 text-sm font-medium text-main-font" htmlFor="message">
                                        <FiMessageSquare size={16} className="text-main-subfont" />
                                        Message
                                    </label>
                                    <input
                                        type="text"
                                        name="message"
                                        value={formInfo?.message}
                                        onChange={(e) => handleFormChange(e)}
                                        id="message"
                                        className="w-full px-4 py-3 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all"
                                        placeholder="Enter notification message"
                                        required
                                    />
                                </div>

                                {/* URL Field */}
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm font-medium text-main-font" htmlFor="url">
                                        <FiLink size={16} className="text-main-subfont" />
                                        URL <span className="text-main-subfont font-normal">(Optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="url"
                                        value={formInfo?.url}
                                        onChange={(e) => handleFormChange(e)}
                                        id="url"
                                        className="w-full px-4 py-3 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all"
                                        placeholder="https://example.com"
                                    />
                                </div>

                                {/* Icon Upload */}
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm font-medium text-main-font" htmlFor="icon">
                                        <FiImage size={16} className="text-main-subfont" />
                                        Icon <span className="text-main-subfont font-normal">(Optional)</span>
                                    </label>
                                    <div className="relative">
                                        <ImageDropzone
                                            file={file}
                                            onFile={processIconFile}
                                            accept='image/png, image/jpg, image/jpeg, image/gif'
                                        />
                                        <p className={`text-xs mt-1.5 ${fileStatus['image'] === 'error' ? 'text-main-delete' : 'text-main-subfont'}`}>
                                            Supported: PNG, JPG, GIF — drag, click or paste
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end mt-6 pt-6 border-t border-main-border">
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-main-blue text-main-white font-medium rounded-xl hover:bg-main-blue/90 focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:ring-offset-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {sending ? <CircularProgress size={18} color="inherit" /> : <BellRing size={20} />}
                                    {sending ? 'Sending...' : 'Send Notification'}
                                </button>
                            </div>
                        </form>
                    </motion.div>

                    {/* Notification History */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden"
                    >
                        <div className="p-6 border-b border-main-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold text-main-font">Notification History</h2>
                                <p className="text-sm text-main-subfont mt-0.5">{data.length} notification{data.length !== 1 ? 's' : ''} sent</p>
                            </div>
                            {selectedUsers.length > 0 && (
                                <button
                                    onClick={() => handleDeleteSelected(null)}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-main-delete/10 text-main-delete font-medium rounded-xl hover:bg-main-delete/20 transition-colors"
                                >
                                    <FiTrash2 size={16} />
                                    Delete Selected ({selectedUsers.length})
                                </button>
                            )}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-main-th">
                                        <th className="px-6 py-4 text-left">
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                                className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            />
                                        </th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Icon</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Title</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Message</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Platform</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">URL</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-main-border">
                                    {loader ? (
                                        <tr><td colSpan="9" className="p-0"><TableSkeleton rows={6} cols={7} /></td></tr>
                                    ) : data.length > 0 ?
                                        data.map((record, index) => (
                                            <tr key={record._id} className="hover:bg-main-thHover transition-colors">
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(record._id)}
                                                        onChange={() => handleSelectUser(record._id)}
                                                        className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className="text-sm text-main-subfont font-medium">{index + 1}</span>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <ImageWithShimmer
                                                        src={record.n_icon || img1}
                                                        alt="icon"
                                                        className="w-12 h-12 rounded-xl bg-main-bg border border-main-border"
                                                        onError={(e) => { e.target.src = img1 }}
                                                    />
                                                </td>
                                                <td className="px-4 py-4">
                                                    <p className="text-sm font-medium text-main-font max-w-[150px] truncate" title={record.n_title}>
                                                        {record.n_title}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <p className="text-sm text-main-subfont max-w-[200px] truncate" title={record.n_message}>
                                                        {record.n_message}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-4">
                                                    {getPlatformBadge(record.n_platform)}
                                                </td>
                                                <td className="px-4 py-4">
                                                    {record.n_url ? (
                                                        <a
                                                            href={record.n_url}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-main-blue bg-main-th rounded-lg hover:bg-main-thHover transition-colors"
                                                        >
                                                            <FiExternalLink size={12} />
                                                            Open
                                                        </a>
                                                    ) : (
                                                        <span className="text-xs text-main-subfont">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className="text-sm text-main-subfont">{formatDate(record.createdAt)}</span>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleSend(record)}
                                                            className="p-2 rounded-lg text-main-blue bg-main-th hover:bg-main-thHover transition-colors"
                                                            title="Resend Notification"
                                                        >
                                                            <IoIosSend size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteSelected(record._id)}
                                                            className="p-2 rounded-lg text-main-delete bg-main-th hover:bg-main-thHover transition-colors"
                                                            title="Delete Notification"
                                                        >
                                                            <MdDelete size={18} />
                                                        </button>

                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                        : (
                                            <tr>
                                                <td colSpan="9" className="px-4 py-12">
                                                    <NotFound />
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </motion.div>

                    {/* Resend Modal */}
                    <AnimatePresence>
                    {showResendModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.94, y: 16 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.94, y: 16 }}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                                className="bg-main-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                            >
                                <div className="p-6 border-b border-main-border bg-main-th">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-main-blue/10 text-main-blue">
                                            <IoIosSend size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-main-font">Resend Notification</h3>
                                            <p className="text-sm text-main-subfont">Choose target platform</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6">
                                    {/* Preview */}
                                    <div className="mb-6 p-4 bg-main-bg rounded-xl border border-main-border">
                                        <p className="text-xs text-main-subfont uppercase tracking-wider mb-2">Preview</p>
                                        <p className="font-medium text-main-font text-sm">{resendInfo?.n_title}</p>
                                        <p className="text-main-subfont text-sm mt-1 line-clamp-2">{resendInfo?.n_message}</p>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-main-font">Select Platform</label>
                                        <FormControl fullWidth>
                                            <Select
                                                value={resendPlatform}
                                                onChange={(e) => setResendPlatform(e.target.value)}
                                                sx={{ borderRadius: '12px', backgroundColor: '#F9FAFE', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#CFD1D3' } }}
                                            >
                                                <MenuItem value="both">Both (Android &amp; iOS)</MenuItem>
                                                <MenuItem value="android">Android Only</MenuItem>
                                                <MenuItem value="ios">iOS Only</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>

                                <div className="p-6 border-t border-main-border bg-main-bg flex gap-3 justify-end">
                                    <button
                                        onClick={() => setShowResendModal(false)}
                                        className="px-5 py-2.5 text-main-font font-medium bg-main-white border border-main-border rounded-xl hover:bg-main-th transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleResendConfirm}
                                        disabled={working}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-main-blue text-main-white font-medium rounded-xl hover:bg-main-blue/90 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {working && <CircularProgress size={16} color="inherit" />}
                                        {working ? 'Sending...' : 'Resend Now'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                    </AnimatePresence>
            </div>
        </>
    )
}

export default Notification